import { useMemo, useState } from "react";
import { newsList } from "@/mocks/news";
import CategoryHero from "@/components/feature/CategoryHero";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import NewsFilters, { type NewsFilterValue } from "./components/NewsFilters";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 4;

const NEWEST_DATE = newsList.reduce((max, item) => (item.date > max ? item.date : max), newsList[0].date);

const withinDays = (dateStr: string, days: number) => {
  const diff = new Date(NEWEST_DATE).getTime() - new Date(dateStr).getTime();
  return diff <= days * 24 * 60 * 60 * 1000;
};

export default function NewsList() {
  useDocumentMeta(
    "娱乐资讯 - 色花堂",
    "色花堂娱乐资讯频道，汇集明星动态、行业趋势与活动现场报道，支持按时间、类型与热度筛选最新娱乐新闻。",
  );
  const [filter, setFilter] = useState<NewsFilterValue>({ time: "all", category: "全部", sort: "latest" });
  const [page, setPage] = useState(1);

  const handleFilterChange = (value: NewsFilterValue) => {
    setFilter(value);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const list = newsList.filter((item) => {
      if (filter.category !== "全部" && item.category !== filter.category) return false;
      if (filter.time === "week" && !withinDays(item.date, 7)) return false;
      if (filter.time === "month" && !withinDays(item.date, 30)) return false;
      if (filter.time === "quarter" && !withinDays(item.date, 90)) return false;
      return true;
    });
    return list.sort((a, b) => (filter.sort === "hot" ? b.views - a.views : a.date < b.date ? 1 : -1));
  }, [filter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => handleFilterChange({ time: "all", category: "全部", sort: "latest" });

  return (
    <div>
      <CategoryHero
        title="娱乐资讯"
        description="汇集明星动态、行业趋势与活动现场的第一手报道，按时间、类型与热度快速筛选你关心的娱乐新闻。"
        icon="ri-newspaper-line"
        crumbs={[{ label: "首页", to: "/" }, { label: "娱乐资讯" }]}
      />

      <div className="px-4 py-8 md:px-8">
        <div className="mx-auto w-full max-w-[1280px]">
          <NewsFilters value={filter} onChange={handleFilterChange} />

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 条资讯
            </p>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-4 flex flex-col gap-4">
                {pageItems.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-4">
              <EmptyState
                icon="ri-search-eye-line"
                title="没有符合条件的资讯"
                description="试着调整时间范围或新闻类型，也可以点击下方按钮重置全部筛选条件。"
                actionLabel="重置筛选条件"
                onAction={reset}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}